import React from "react";
import PropTypes from "prop-types";
import { Card, Col, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Flex from "components/common/Flex";
import { PieChart } from "echarts/charts";
import * as echarts from "echarts/core";
import BasicECharts from "components/common/BasicEChart";
import {
  GridComponent,
  TitleComponent,
  TooltipComponent,
} from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import { useAppContext } from "providers/AppProvider";

// Register ECharts components
echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  PieChart,
  CanvasRenderer,
]);

// Claims Data
const claimsData = [
  { name: "0 Claims", value: 0, color: "#6c757d" },
  { name: "865 Claims", value: 14.92, color: "#20c997" },
  { name: "1.5k Claims", value: 26.47, color: "#0dcaf0" },
  { name: "3.4k Claims", value: 58.61, color: "#fd7e14" },
];

const claimsRadius = ["50%", "70%"];
const claimsTotal = "5.8k Claims";

// ECharts options
const getOptions = (getThemeColor, data, radius, total) => ({
  color: data.map((item) => item.color),
  tooltip: {
    padding: [7, 10],
    transitionDuration: 0,
    backgroundColor: getThemeColor("gray-100"),
    borderColor: getThemeColor("gray-300"),
    textStyle: { color: getThemeColor("gray-1100") },
    borderWidth: 1,
    formatter: (params) =>
      `<strong>${params.data.name}:</strong> ${params.percent.toFixed(2)}%`,
  },
  series: [
    {
      name: "Total",
      type: "pie",
      radius,
      avoidLabelOverlap: false,
      emphasis: { scale: false },
      itemStyle: {
        borderWidth: 2,
        borderColor: getThemeColor("gray-100"),
      },
      label: {
        show: true,
        position: "center",
        formatter: `{a|${total}}`,
        rich: {
          a: {
            fontSize: 14,
            color: getThemeColor("gray-1100"),
            lineHeight: 15,
          },
        },
      },
      data,
    },
  ],
});

// Legend Item
const ClaimItem = ({ item, total }) => {
  const { name, color, value } = item;
  const percentage = value.toFixed(2);
  return (
    <Flex
      alignItems="center"
      justifyContent="between"
      className="fw-semibold fs-11 mb-1"
    >
      <p className="mb-0 d-flex align-items-center text-capitalize">
        <FontAwesomeIcon icon="circle" className="me-2" style={{ color }} />
        {name}
      </p>
      <div>{percentage}%</div>
    </Flex>
  );
};

// Main Component
const ClaimsNumberDoughnutChart = ({
  data = claimsData,
  radius = claimsRadius,
  totalLabel = claimsTotal,
}) => {
  const { getThemeColor } = useAppContext();

  return (
    <Col xs={12} sm={6} lg={3} className="">
      <Card className="h-100 shadow-sm  p-3">
        <Card.Header as="h6" className="fs-10 fw-bold text-800 p-2">
          Number of Claims
        </Card.Header>
        <Card.Body className="p-2 m-0">
          <Row className="align-items-center">
            {/* Left – Legend */}
            <Col xs={12} md={6}>
              {data.map((item, index) => (
                <ClaimItem item={item} key={index} total={totalLabel} />
              ))}
            </Col>

            {/* Right – Chart */}
            <Col xs={12} md={6} className="text-center">
              <BasicECharts
                echarts={echarts}
                options={getOptions(getThemeColor, data, radius, totalLabel)}
                style={{
                  width: "10.5rem",
                  height: "10.5rem",
                  margin: "0 auto",
                }}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </Col>
  );
};

// PropTypes
ClaimItem.propTypes = {
  item: PropTypes.shape({
    name: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    value: PropTypes.number.isRequired,
  }),
  total: PropTypes.string.isRequired,
};

ClaimsNumberDoughnutChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
    })
  ),
  radius: PropTypes.arrayOf(PropTypes.string),
  totalLabel: PropTypes.string,
};

ClaimsNumberDoughnutChart.defaultProps = {
  data: claimsData,
  radius: claimsRadius,
  totalLabel: claimsTotal,
};

export default ClaimsNumberDoughnutChart;
